/**
 * Error Normalization Helper
 * 
 * Safely converts any error type to a displayable string.
 * Handles:
 * - Plain strings
 * - Error objects
 * - Axios/fetch error responses
 * - API error payloads with {error_code, message} or {detail}
 * - Generic objects
 */

export function normalizeError(err) {
  // Already a string
  if (typeof err === 'string') {
    return err;
  }
  
  // Null/undefined
  if (err == null) {
    return 'Unknown error';
  }
  
  // Error object
  if (err instanceof Error) {
    return err.message || 'Unknown error';
  }
  
  // Axios error with response
  if (err.response?.data) {
    return normalizeError(err.response.data);
  }
  
  // API error payload with error_code and message
  if (typeof err === 'object') {
    // Handle {error_code, message} format
    if (err.error_code && err.message) {
      return `${err.error_code}: ${err.message}`;
    }
    
    // Handle {detail: {error_code, message}} nested format
    if (err.detail && typeof err.detail === 'object') {
      if (err.detail.error_code && err.detail.message) {
        return `${err.detail.error_code}: ${err.detail.message}`;
      }
      // Try to stringify detail object
      try {
        return JSON.stringify(err.detail);
      } catch {
        return 'Error occurred';
      }
    }
    
    // Handle {detail: "string"} format
    if (err.detail && typeof err.detail === 'string') {
      return err.detail;
    }
    
    // Handle {message: "string"} format
    if (err.message && typeof err.message === 'string') {
      return err.message;
    }
    
    // Handle {error: "string"} format
    if (err.error && typeof err.error === 'string') {
      return err.error;
    }
    
    // Last resort: stringify the object
    try {
      const str = JSON.stringify(err);
      // Don't show huge objects
      if (str.length > 200) {
        return 'An error occurred';
      }
      return str;
    } catch {
      return 'An error occurred';
    }
  }
  
  // Fallback
  return String(err) || 'Unknown error';
}

/**
 * Extract error message from axios error response
 * Convenience wrapper for common axios error handling pattern
 */
export function getAxiosErrorMessage(error, fallback = 'Request failed') {
  if (error.response?.data) {
    return normalizeError(error.response.data);
  }
  if (error.message) {
    return error.message;
  }
  return fallback;
}

export default normalizeError;
